import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Monitor,
  History,
  Loader2,
  Activity,
  Clock,
} from "lucide-react";
import { timeEntriesApi } from "../../services/api";
import ScreenCaptureWidget from "../time/ScreenCaptureWidget";
import AiTimeSuggestionsPanel from "../billing/AiTimeSuggestionsPanel";
import ActivityTimeline from "../billing/ActivityTimeline";

// ─── Explanation Card ───────────────────────────────────────────────────────

const ExplanationCard: React.FC<{
  icon: React.FC<any>;
  title: string;
  description: string;
  badge?: string;
}> = ({ icon: Icon, title, description, badge }) => (
  <div className="flex items-start gap-3 mb-3 px-1">
    <div className="p-2 rounded-lg bg-emerald-50 flex-shrink-0">
      <Icon className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {badge && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{description}</p>
    </div>
  </div>
);

// ─── AI Assistant Tab ───────────────────────────────────────────────────────

interface Props {
  onDataChange?: () => void;
}

/** YYYY-MM-DD in the user's local time zone (toISOString would give the UTC date). */
const toLocalDateString = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const AiAssistantTab: React.FC<Props> = ({ onDataChange }) => {
  // Reconstruct Day state
  const [showReconstructModal, setShowReconstructModal] = useState(false);
  const [reconstructing, setReconstructing] = useState(false);
  const [reconstructData, setReconstructData] = useState<any>(null);
  const [reconstructDate, setReconstructDate] = useState(
    toLocalDateString(new Date())
  );

  const handleReconstructDay = async () => {
    if (reconstructing) return;
    setReconstructing(true);
    setReconstructData(null);
    try {
      const { data } = await timeEntriesApi.reconstructDay(reconstructDate);
      setReconstructData(data);
      onDataChange?.();
    } catch (err) {
      console.error("Failed to reconstruct day:", err);
    } finally {
      setReconstructing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Overview Card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="bg-gradient-to-r from-[#0F5132] to-[#198754] rounded-xl p-6 text-white"
      >
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="h-6 w-6" />
          <h2 className="text-lg font-bold">AI Time Assistant</h2>
        </div>
        <p className="text-emerald-100 text-sm">
          Kavoa uses AI to help you capture billable time you might miss. Below
          are the tools available to automate your time tracking.
        </p>
      </motion.div>

      {/* Section 1: Screen Capture */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
      >
        <ExplanationCard
          icon={Monitor}
          title="Screen Capture"
          description="Record your screen and AI detects what you're working on, matching activities to matters. Captured frames are analyzed and immediately deleted — never stored."
          badge="Elite"
        />
        <ScreenCaptureWidget onDataChange={onDataChange} />
      </motion.div>

      {/* Section 2: AI Suggestions */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1 }}
      >
        <ExplanationCard
          icon={Sparkles}
          title="AI Suggestions"
          description="Based on your recent activities — emails sent, documents reviewed, calls made — AI generates time entry suggestions with professional billing narratives. Review, edit, and approve them to add to your timesheet."
        />
        <AiTimeSuggestionsPanel onDataChange={onDataChange} />
      </motion.div>

      {/* Section 3: Reconstruct Day */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.15 }}
      >
        <ExplanationCard
          icon={History}
          title="Reconstruct Day"
          description="Forgot to track your time? Pick a date and AI will analyze all your recorded activities for that day to reconstruct what you worked on."
        />
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={reconstructDate}
              onChange={(e) => setReconstructDate(e.target.value)}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
            <button
              onClick={handleReconstructDay}
              disabled={reconstructing}
              className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "var(--color-primary)")
              }
            >
              {reconstructing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {reconstructing ? "Analyzing..." : "Reconstruct"}
            </button>
          </div>

          {/* Results */}
          {reconstructData && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 space-y-4"
            >
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">
                    {reconstructData.summary.activitiesCount}
                  </p>
                  <p className="text-xs text-slate-500">Activities</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">
                    {reconstructData.summary.existingEntriesCount}
                  </p>
                  <p className="text-xs text-slate-500">Already Logged</p>
                </div>
                <div className="rounded-xl bg-emerald-50 p-3 text-center">
                  <p
                    className="text-2xl font-bold"
                    style={{ color: "var(--color-primary)" }}
                  >
                    {reconstructData.summary.suggestionsCount}
                  </p>
                  <p className="text-xs text-slate-500">New Suggestions</p>
                </div>
              </div>

              {/* Timeline */}
              {reconstructData.timeline?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    Day Timeline
                  </h4>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {reconstructData.timeline.map((item: any, i: number) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <span className="text-xs text-slate-400 w-16 flex-shrink-0 pt-0.5">
                          {new Date(item.occurredAt).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                        <div className="h-2 w-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-slate-700">{item.description}</p>
                          {item.matterId?.title && (
                            <p className="text-xs text-slate-400">
                              {item.matterId.title}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggestions notice */}
              {reconstructData.suggestions?.length > 0 && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <p
                    className="text-sm font-medium"
                    style={{ color: "var(--color-primary)" }}
                  >
                    {reconstructData.suggestions.length} new suggestion
                    {reconstructData.suggestions.length !== 1 ? "s" : ""} added
                    to your AI Suggestions panel for review.
                  </p>
                </div>
              )}

              {/* Empty state */}
              {reconstructData.timeline?.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  No activity found for this date.
                </p>
              )}
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Section 4: Activity Feed */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2 }}
      >
        <ExplanationCard
          icon={Activity}
          title="Activity Feed"
          description="A chronological log of all captured activities — emails, calls, document edits, and more. Click any activity to create a time entry from it."
        />
        <ActivityTimeline onCreateFromActivity={() => {}} />
      </motion.div>
    </div>
  );
};

export default AiAssistantTab;
